import { useLayoutEffect, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * A stat tile's title at `maxPx`, shrunk only as far as it must be to fit its
 * parent box (the tile's title area), down to `minPx`.
 */
export default function StatTitle({ children, maxPx, minPx, sx }) {
  const ref = useRef(null);
  const [size, setSize] = useState(maxPx);

  useLayoutEffect(() => {
    const el = ref.current;
    const box = el?.parentElement;
    if (!el || !box) return undefined;

    const fit = () => {
      let px = maxPx;
      el.style.fontSize = `${px}px`;
      while (px > minPx && (el.scrollHeight > box.clientHeight || el.scrollWidth > box.clientWidth)) {
        px -= 1;
        el.style.fontSize = `${px}px`;
      }
      setSize(px);
    };

    fit();
    // Widths change once the web font arrives, and with the screen size.
    document.fonts?.ready.then(fit);
    const observer = new ResizeObserver(fit);
    observer.observe(box);
    return () => observer.disconnect();
  }, [children, maxPx, minPx]);

  return (
    <Typography ref={ref} sx={{ ...sx, fontSize: `${size}px` }}>
      {children}
    </Typography>
  );
}

StatTitle.propTypes = {
  children: PropTypes.node,
  maxPx: PropTypes.number.isRequired,
  minPx: PropTypes.number.isRequired,
  sx: PropTypes.object,
};
